import { orgQueryKeys } from '@/config/queryKeys';
import { ROUTE_PATHS, adminRoles, publicRoutes } from '@/constants';
// Handle post-login redirect
import { usePostLoginRedirect } from '@/hooks/usePostLoginRedirect';
import { handleUnauthorized } from '@/lib/shared/auth-shared';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { ROLES } from '@/types';
import type { AuthData } from '@/types/auth';
import { Layout, Sidebar } from '@components/index.ts';
import { mainContent } from '@components/Layout/styles.module.css';
import type { SidebarLinkProps } from '@components/Sidebar/types.js';
import { Toaster } from '@nautilusbio/nb-design-system/Toaster';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { FormDevtoolsPanel } from '@tanstack/react-form-devtools';
import '@/styles/components/ag-grid-overrides.css';
import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import { HeadContent, Outlet, Scripts, createRootRouteWithContext, useLocation } from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel, Separator, usePanelRef } from 'react-resizable-panels';

import { useAuthStatus } from '../hooks/useAuthStatus';
import pageStyles from '../styles/pages/index.module.css';

type RouterContext = {
  authentication: AuthData;
  queryClient: QueryClient;
};

const tanstackPlugins = [
  {
    name: 'TanStack Query',
    render: <ReactQueryDevtoolsPanel />,
    defaultOpen: false,
  },
  {
    name: 'TanStack Router',
    render: <TanStackRouterDevtoolsPanel />,
    defaultOpen: false,
  },
  {
    name: 'TanStack Form',
    render: <FormDevtoolsPanel theme="dark" />,
    defaultOpen: false,
  },
];
const tanstackConfig = { hideUntilHover: true };
// TODO: Error/404 Handling

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: ({ context }) => {
    // Skip auth check for login page
    if (window.location.pathname === ROUTE_PATHS.LOGIN) {
      return;
    }

    // Don't redirect if we're still loading the auth state
    // This is critical - we must wait for the auth query to complete
    if (context.authentication.status === 'PENDING') {
      return;
    }

    // Only redirect if explicitly unauthenticated (not pending)
    // The auth query will determine the actual state
    if (context.authentication.status === 'UNAUTHENTICATED') {
      handleUnauthorized();
      return;
    }
  },
  component: () => {
    const location = useLocation();

    const showSidebar = useMemo(
      () => !publicRoutes.some(route => location.pathname.includes(route)),
      [location?.pathname],
    );

    const { user } = useAuthStatus();

    // Fetch organizations for the logged-in user
    const { data: orgs } = useQuery({
      enabled: !!user,
      queryKey: orgQueryKeys.list(),
      queryFn: fetchOrganizations,
    });

    // Sidebar should be disabled if user is logged in and has no orgs
    const sidebarDisabled = !!user && orgs && orgs.length === 0;

    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [isHovering, setIsHovering] = useState<boolean>(false);
    const dragTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const didMountRef = useRef(false);
    const { t } = useTranslation('sidebar');

    usePostLoginRedirect();

    const topLevelLinks: SidebarLinkProps[] = useMemo(
      () => [
        {
          children: t('stu'),
          icon: 'notebook-pen',
          preload: 'intent',
          to: '/studies',
        },
        {
          children: t('runs'),
          icon: 'flask-conical',
          preload: 'intent',
          to: '/instrument_runs',
        },
        {
          children: t('sam'),
          icon: 'microscope',
          roles: [ROLES[0]],
          preload: 'intent',
          to: '/samples',
        },
        {
          children: t('use'),
          icon: 'user-round-cog',
          roles: adminRoles,
          to: '/users',
        },
        {
          children: t('org'),
          icon: 'briefcase-business',
          roles: adminRoles,
          to: '/organizations',
        },
        // { children: t('fle'), icon: 'computer', to: '/fleet' },
        { children: t('aut'), icon: 'key', roles: adminRoles, to: '/auth' },
      ],
      [t],
    );
    const sidebarRef = usePanelRef();

    const resizerDblClickHandler = useCallback(() => {
      return sidebarRef?.current?.isCollapsed() ? sidebarRef?.current?.expand() : sidebarRef?.current?.collapse();
    }, [sidebarRef]);

    const handleLayoutChange = useCallback(() => {
      if (!didMountRef.current) {
        didMountRef.current = true;
        return;
      }

      setIsDragging(true);

      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }

      dragTimeoutRef.current = setTimeout(() => {
        setIsDragging(false);
      }, 100);
    }, []);

    useEffect(() => {
      return () => {
        if (dragTimeoutRef.current) {
          clearTimeout(dragTimeoutRef.current);
        }
      };
    }, []);

    const handleSeparatorMouseEnter = useCallback(() => {
      setIsHovering(true);
    }, []);
    const handleSeparatorMouseLeave = useCallback(() => {
      setIsHovering(false);
    }, []);

    return (
      <>
        <HeadContent />

        <Scripts />

        <Layout disabled={!user} onLayoutChange={handleLayoutChange}>
          {showSidebar && (
            <>
              <Sidebar
                disabled={sidebarDisabled}
                isInteracting={isDragging || isHovering}
                links={topLevelLinks}
                sidebarRef={sidebarRef}
              />
              <Separator
                className={clsx(
                  pageStyles.resizeHandle,
                  isDragging && pageStyles.resizing,
                  isHovering && pageStyles.resizeHover,
                )}
                onDoubleClick={resizerDblClickHandler}
                onMouseEnter={handleSeparatorMouseEnter}
                onMouseLeave={handleSeparatorMouseLeave}
              />
            </>
          )}

          <Panel className={clsx(mainContent, 'maincontent-panel')}>
            <Outlet />
          </Panel>
        </Layout>

        <Toaster />

        {import.meta.env.VITE_ENABLE_DEVTOOLS === 'true' && (
          <TanStackDevtools config={tanstackConfig} plugins={tanstackPlugins} />
        )}
      </>
    );
  },
  // No longer passing selectedOrganization into context; access it directly from the store where needed
  head: () => ({
    meta: [
      {
        name: 'Nautilus Biotechnology - Customer Portal',
        content: 'App for interacting with instruments.',
      },
      {
        title: 'Nautilus Biotechnology - Customer Portal',
      },
    ],
  }),
});
